import {
  getServiceOption,
  getServiceReasonKey,
} from '../../hooks/useScheduleEntities.utils';

export const noop = () => {};
export const serviceReasonSearchDebounceMs = 250;

const getOptionText = (option) =>
  [option?.search, option?.text, option?.content, option?.value]
    .filter(Boolean)
    .join(' ');

export const createOptionSearch = (option) => getOptionText(option);

export const createServiceSetNameSearch = (option) =>
  [option?.text, option?.comment].filter(Boolean).join(' ');

export const getArrayValue = (value) =>
  Array.isArray(value) ? value : [value].filter(Boolean);

export const validateRequiredArray = (message) => (_rule, value, callback) => {
  if (Array.isArray(value) && value.length) {
    callback();
    return;
  }

  callback(message);
};

export const getReasonText = (service, reasonId, index) => {
  if (service.reasons?.[index]?.name) {
    return service.reasons[index].name;
  }

  if (service.reasonNames?.[index]) {
    return service.reasonNames[index];
  }

  return reasonId;
};

const getEntityId = (entity) => {
  if (entity && typeof entity === 'object') {
    return entity.id ?? entity.value;
  }

  return entity;
};

export const isImmediateReason = (reason) =>
  Boolean(reason?.isImmediate ?? reason?.data?.isImmediate);

export const isServiceReasonImmediate = (service, reasonId, index) => {
  if (isImmediateReason(service.reasons?.[index])) {
    return true;
  }

  return (service.reasons || []).some((reason) => {
    const entityId = getEntityId(reason);
    const possibleReasonId = reason.reasonId ?? reason.id ?? entityId;

    return (
      isImmediateReason(reason) &&
      (String(entityId) === String(reasonId) ||
        String(possibleReasonId) === String(reasonId))
    );
  });
};

const getUniqueKeys = (values = []) => [
  ...new Set(
    values.filter((value) => value !== undefined && value !== null).map(String)
  ),
];

export const getSelectionKey = (values = []) => getUniqueKeys(values).join('|');

export const hasSameSelection = (firstValues = [], secondValues = []) =>
  getUniqueKeys(firstValues).sort().join('|') ===
  getUniqueKeys(secondValues).sort().join('|');

const getUniqueValues = (values = []) => {
  const valueKeys = new Set();

  return values.filter((value) => {
    if (value === undefined || value === null) {
      return false;
    }

    const valueKey = String(value);

    if (valueKeys.has(valueKey)) {
      return false;
    }

    valueKeys.add(valueKey);
    return true;
  });
};

const addUniqueValues = (values = [], addedValues = []) =>
  getUniqueValues([...values, ...addedValues]);

const removeValues = (values = [], removedValues = []) => {
  const removedValueKeys = new Set(removedValues.map(String));

  return getUniqueValues(values).filter(
    (value) => !removedValueKeys.has(String(value))
  );
};

export const toggleServiceSelection = ({
  selectedReasonIds = [],
  selectedServiceIds = [],
  service,
}) => {
  const serviceReasonIds = (service?.reasons || []).map(
    (reason) => reason.value
  );
  const selectedReasonIdSet = new Set(selectedReasonIds.map(String));
  const serviceIsSelected = selectedServiceIds.some(
    (serviceId) => String(serviceId) === String(service?.value)
  );
  const allReasonsAreSelected = Boolean(
    serviceReasonIds.length &&
      serviceReasonIds.every((reasonId) =>
        selectedReasonIdSet.has(String(reasonId))
      )
  );
  const shouldDeselect = serviceIsSelected || allReasonsAreSelected;

  return {
    selectedReasonIds: shouldDeselect
      ? removeValues(selectedReasonIds, serviceReasonIds)
      : addUniqueValues(selectedReasonIds, serviceReasonIds),
    selectedServiceIds: shouldDeselect
      ? removeValues(selectedServiceIds, [service?.value])
      : addUniqueValues(selectedServiceIds, [service?.value]),
  };
};

export const toggleReasonSelection = ({
  reasonId,
  selectedReasonIds = [],
  selectedServiceIds = [],
  serviceReasonGroups = [],
}) => {
  const reasonKey = String(reasonId);
  const service = serviceReasonGroups.find((item) =>
    item.reasonKeys.some((itemReasonId) => String(itemReasonId) === reasonKey)
  );
  const serviceIsSelected = selectedServiceIds.some(
    (serviceId) => String(serviceId) === String(service?.value)
  );
  const serviceReasonIds = (service?.reasons || []).map(
    (reason) => reason.value
  );
  const currentReasonIds = serviceIsSelected
    ? addUniqueValues(selectedReasonIds, serviceReasonIds)
    : getUniqueValues(selectedReasonIds);
  const reasonIsSelected = currentReasonIds.some(
    (itemReasonId) => String(itemReasonId) === reasonKey
  );
  const nextReasonIds = reasonIsSelected
    ? removeValues(currentReasonIds, [reasonId])
    : addUniqueValues(currentReasonIds, [reasonId]);

  if (!service) {
    return {
      selectedReasonIds: nextReasonIds,
      selectedServiceIds: getUniqueValues(selectedServiceIds),
    };
  }

  const nextReasonIdSet = new Set(nextReasonIds.map(String));
  const allReasonsAreSelected = Boolean(
    serviceReasonIds.length &&
      serviceReasonIds.every((itemReasonId) =>
        nextReasonIdSet.has(String(itemReasonId))
      )
  );

  return {
    selectedReasonIds: nextReasonIds,
    selectedServiceIds: allReasonsAreSelected
      ? addUniqueValues(selectedServiceIds, [service.value])
      : removeValues(selectedServiceIds, [service.value]),
  };
};

export const getSelectedReasonIds = (service) => {
  if (Array.isArray(service.reasonIds)) {
    return service.reasonIds;
  }

  if (Array.isArray(service.reasons)) {
    return service.reasons.map(getEntityId);
  }

  return [];
};

const getFilteredServiceReasonData = (service, showImmediateOnly) => {
  const reasonIds = getSelectedReasonIds(service);

  if (!showImmediateOnly || !reasonIds.length) {
    return {
      reasonIds: service.reasonIds,
      reasonNames: service.reasonNames,
      reasons: service.reasons,
    };
  }

  const filteredReasons = reasonIds
    .map((reasonId, index) => ({
      id: reasonId,
      index,
      isImmediate: isServiceReasonImmediate(service, reasonId, index),
      name: getReasonText(service, reasonId, index),
      reason: service.reasons?.[index],
    }))
    .filter((reason) => reason.isImmediate);

  return {
    reasonIds: filteredReasons.map((reason) => reason.id),
    reasonNames: filteredReasons.map((reason) => reason.name),
    reasons: filteredReasons.map((reason) => ({
      ...(reason.reason || {}),
      id: reason.reason?.id ?? reason.id,
      isImmediate: true,
      name: reason.reason?.name || reason.name,
    })),
  };
};

export const getFilteredServiceSetsByServices = ({
  serviceSets = [],
  serviceFilterValues = [],
  showImmediateOnly = false,
}) => {
  const serviceFilterSet = new Set(serviceFilterValues.map(String));

  if (!serviceFilterSet.size && !showImmediateOnly) {
    return serviceSets;
  }

  return serviceSets.map((serviceSet) => {
    const services = (serviceSet.services || [])
      .filter(
        (service) =>
          !serviceFilterSet.size ||
          serviceFilterSet.has(String(service.serviceId ?? service.id))
      )
      .map((service) => {
        const reasonData = getFilteredServiceReasonData(
          service,
          showImmediateOnly
        );

        if (showImmediateOnly && !reasonData.reasonIds.length) {
          return null;
        }

        return {
          ...service,
          ...reasonData,
        };
      })
      .filter(Boolean);

    return { ...serviceSet, services };
  });
};

export const getServiceSetOption = (serviceSet) => ({
  key: serviceSet.id,
  value: serviceSet.id,
  text: serviceSet.name,
  content: serviceSet.name,
  comment: serviceSet.comment,
  search: [
    serviceSet.name,
    ...(serviceSet.services || []).flatMap((service) => [
      service.serviceName,
      ...(service.reasonNames || []),
      ...(service.reasons || []).map((reason) => reason.name),
    ]),
  ]
    .filter(Boolean)
    .join(' '),
});

export const getServiceOptionsFromServiceSets = (serviceSets = []) => {
  const serviceOptionsByKey = new Map();

  serviceSets.forEach((serviceSet) => {
    (serviceSet.services || []).forEach((service) => {
      const serviceId = service.serviceId ?? service.id;

      if (serviceId === undefined || serviceId === null) {
        return;
      }

      const serviceKey = String(serviceId);

      if (!serviceOptionsByKey.has(serviceKey)) {
        const serviceName = service.serviceName || service.name || serviceId;

        serviceOptionsByKey.set(serviceKey, {
          key: serviceId,
          value: serviceId,
          text: serviceName,
          content: serviceName,
        });
      }
    });
  });

  return [...serviceOptionsByKey.values()];
};

export const getSelectedFirstOptions = (options = [], selectedValues = []) => {
  const normalizedSelectedValues = Array.isArray(selectedValues)
    ? selectedValues
    : [selectedValues].filter(Boolean);
  const selectedValueSet = new Set(normalizedSelectedValues.map(String));

  if (!selectedValueSet.size) {
    return options;
  }

  return options
    .map((option, index) => ({ option, index }))
    .sort((first, second) => {
      const firstSelected = selectedValueSet.has(String(first.option.value));
      const secondSelected = selectedValueSet.has(String(second.option.value));

      if (firstSelected === secondSelected) {
        return first.index - second.index;
      }

      return firstSelected ? -1 : 1;
    })
    .map(({ option }) => option);
};

export const sortSelectedFirst = (items = [], isSelected) =>
  items
    .map((item, index) => ({ item, index }))
    .sort((first, second) => {
      const firstSelected = isSelected(first.item);
      const secondSelected = isSelected(second.item);

      if (firstSelected === secondSelected) {
        return first.index - second.index;
      }

      return firstSelected ? -1 : 1;
    })
    .map(({ item }) => item);

const isMatchedSearch = (value, searchValue) => {
  const normalizedSearch = searchValue.trim().toLocaleLowerCase('ru-RU');

  if (!normalizedSearch) {
    return true;
  }

  return String(value).toLocaleLowerCase('ru-RU').includes(normalizedSearch);
};

export const getServiceReasonGroupsFromTree = (
  services = [],
  existingGroups = []
) => {
  const existingServices = new Map(
    existingGroups.map((service) => [String(service.value), service])
  );
  const existingReasons = new Map(
    existingGroups.flatMap((service) =>
      service.reasons.map((reason) => [String(reason.value), reason])
    )
  );

  return services.map((service) => {
    const reasons = service.reasons.map((reason) => {
      const value = getServiceReasonKey(service.id, reason.id);

      return {
        ...getServiceOption(reason),
        value,
        reasonId: reason.id,
        serviceId: service.id,
        serviceName: service.name,
        isSelected: reason.isSelected,
        isImmediate:
          reason.isImmediate ??
          existingReasons.get(value)?.isImmediate ??
          existingReasons.get(value)?.data?.isImmediate,
        data: {
          ...existingReasons.get(value)?.data,
          parentId: service.id,
          parentName: service.name,
          serviceTypes: reason.serviceTypes,
        },
      };
    });

    return {
      ...getServiceOption(service),
      isSelected: service.isSelected,
      isImmediate:
        service.isImmediate ??
        existingServices.get(String(service.id))?.isImmediate,
      reasonKeys: reasons.map((reason) => reason.value),
      reasons,
    };
  });
};

export const getCreatePreviewServices = ({
  selectedReasonIds,
  selectedServiceIdSet,
  serviceReasonGroups,
}) => {
  const selectedReasonIdSet = new Set((selectedReasonIds || []).map(String));

  return (serviceReasonGroups || [])
    .map((service) => {
      const selectedReasons = (service.reasons || []).filter((reason) =>
        selectedReasonIdSet.has(String(reason.value))
      );

      const isServiceSelected = selectedServiceIdSet
        ? selectedServiceIdSet.has(String(service.value))
        : selectedReasons.length > 0;

      if (!isServiceSelected && !selectedReasons.length) {
        return null;
      }

      return {
        serviceId: service.value,
        serviceName: service.text,
        reasonIds: selectedReasons.map((reason) => reason.reasonId),
        reasonNames: selectedReasons.map((reason) => reason.text),
        reasons: selectedReasons.map((reason) => ({
          id: reason.reasonId,
          isImmediate: isImmediateReason(reason),
          name: reason.text || reason.reasonId,
        })),
        allReasonIds: (service.reasons || []).map((reason) => reason.reasonId),
      };
    })
    .filter(Boolean);
};

export const getVisibleServiceReasonGroups = ({
  debouncedSearchValue,
  immediateFilterValue,
  isServiceSetEditorVisible,
  selectedReasonIdSet,
  selectedServiceIdSet,
  committedReasonIdSet = selectedReasonIdSet,
  committedServiceIdSet = selectedServiceIdSet,
  selectionFilterValue,
  serviceFilterKeys,
  serviceReasonGroups,
  serviceSetFilterSelectionValues,
}) => {
  if (!isServiceSetEditorVisible) {
    return [];
  }

  const serviceFilterSet = new Set(serviceFilterKeys);
  const visibleServices = (serviceReasonGroups || [])
    .filter(
      (service) =>
        !serviceFilterSet.size || serviceFilterSet.has(String(service.value))
    )
    .map((service) => {
      const serviceMatches = isMatchedSearch(
        getOptionText(service),
        debouncedSearchValue
      );
      const searchMatchedReasons = serviceMatches
        ? service.reasons || []
        : (service.reasons || []).filter((reason) =>
            isMatchedSearch(getOptionText(reason), debouncedSearchValue)
          );
      const filteredReasons = searchMatchedReasons.filter((reason) => {
        const isSelected = committedReasonIdSet.has(String(reason.value));

        if (
          immediateFilterValue &&
          !isImmediateReason(service) &&
          !isImmediateReason(reason)
        ) {
          return false;
        }

        if (selectionFilterValue === serviceSetFilterSelectionValues.selected) {
          return isSelected;
        }

        if (
          selectionFilterValue === serviceSetFilterSelectionValues.unselected
        ) {
          return !isSelected;
        }

        return true;
      });
      const visibleReasons = sortSelectedFirst(filteredReasons, (reason) =>
        committedReasonIdSet.has(String(reason.value))
      );

      const isServiceSelected = committedServiceIdSet?.has(
        String(service.value)
      );
      const matchesServiceSelection =
        selectionFilterValue === serviceSetFilterSelectionValues.selected
          ? isServiceSelected
          : selectionFilterValue === serviceSetFilterSelectionValues.unselected
          ? !isServiceSelected
          : true;

      return {
        ...service,
        visibleReasons,
        showServiceWithoutReasons: Boolean(
          selectedServiceIdSet &&
            serviceMatches &&
            matchesServiceSelection &&
            (!immediateFilterValue || isImmediateReason(service)) &&
            selectionFilterValue !== serviceSetFilterSelectionValues.immediate
        ),
      };
    })
    .filter(
      (service) =>
        service.visibleReasons.length || service.showServiceWithoutReasons
    );

  return sortSelectedFirst(
    visibleServices,
    (service) =>
      committedServiceIdSet?.has(String(service.value)) ||
      service.reasonKeys.some((reasonId) =>
        committedReasonIdSet.has(String(reasonId))
      )
  );
};

export const hasSameServiceSelection = (reasonKeys = [], firstSet, secondSet) =>
  reasonKeys.every(
    (reasonId) =>
      firstSet.has(String(reasonId)) === secondSet.has(String(reasonId))
  );
